import type { GameRowValue, } from '../backend.d'
import { useSearchParams } from '@solidjs/router'
import { Component, DEV, createEffect, onMount } from 'solid-js'
import { createMutable } from 'solid-js/store'
import toast from 'solid-toast'
import bindViewCtxToWindowV2 from '../lib/bindViewCtxToWindowV2'
import bindEngineToComponentV2 from '../lib/canvas/bindEngineToComponentV2'
import createEngineForGamePlay from '../lib/core/engine/createEngineForGamePlay'
import { Engine } from '../lib/core/engine/Engine.type'
import syncEngineWithPropsRow from '../lib/core/engine/syncEngineWithPropsRow'
import { StateType } from '../lib/core/state/state_type.enum'
import registerTouchEventsV2 from '../lib/dom/event/registerTouchEventsV2'
import bindCameraToUserPreferences from '../lib/three/camera/bindCameraToUserPreferences'
import createMainCamera from '../lib/three/fn/createMainCamera'
import createMountedMainCanvasElement from '../lib/three/fn/createMountedMainCanvasElement'
import createMountedMainRenderer from '../lib/three/fn/createMountedMainRenderer'
import { setEngineThreeMainCamera } from '../lib/three/fn/getSetEngineThreeMainCamera'
import { setEngineThreeMainRenderer } from '../lib/three/fn/getSetEngineThreeMainRenderer'
import { setEngineToast } from '../lib/three/fn/getSetEngineToast'
import onMountStartCameraNearLastAction from '../lib/three/fn/onMountStartCameraNearLastAction'
import createRedirectIfWrongStateTypeEffect from '../rx/effect/createRedirectIfWrongStateTypeEffect'
import createSetEngineAuthPlayerIdFromAuthProfileSignalEffect from '../rx/effect/createSetEngineAuthPlayerIdFromAuthProfileSignalEffect'
import createSetEngineAuthPlayerIdFromLocalEffect from '../rx/effect/createSetEngineAuthPlayerIdFromLocalEffect'
import useCornerWidgetEngine from '../rx/shared/CornerWidget/useCornerWidgetEngine'
import { getAuthProfile } from '../rx/shared/profile/createAuthProfileSignal'
import registerServerSentEvents from '../rx/sse/registerServerSentEvents'
import useLocalBotPlayer from '../rx/use/useLocalBotPlayer'
import { hasIndexedDB } from '../lib/core/Storage/IndexedDB/hasIndexedDB'
import EngineContext from './EngineContext'
import GameEndedModal from './GameEndedModal'

interface Props {
  // session: ,
  row?: GameRowValue,
  local?: boolean,
}

const componentStateType = StateType.Game

const GamePlayThreeJsCanvas: Component<Props> = (props) => {
  const engine: Engine = createMutable<Engine>(createEngineForGamePlay())

  onMount(() => {
    if (DEV) {
      const [searchParams] = useSearchParams()
      if (searchParams.debug_scenario) {
        import(`../lib/debug/scenario/${searchParams.debug_scenario}.ts`).then((module) => {
          module.default(engine)
        })
        createSetEngineAuthPlayerIdFromAuthProfileSignalEffect(engine, getAuthProfile)
        return
      }
    }
  })


  createEffect(() => {
    if (props.row) {
      syncEngineWithPropsRow(engine, props.row)
    }
  })

  createEffect(() => {
    if (props.local && !hasIndexedDB()) {
      throw new Error('IndexedDB required for local game playback')
    }
    if (props.local) {
      createSetEngineAuthPlayerIdFromLocalEffect(engine)
      useLocalBotPlayer(engine)
    } else {
      createSetEngineAuthPlayerIdFromAuthProfileSignalEffect(engine, getAuthProfile)
      registerServerSentEvents(engine)
    }
  })

  // registerMouseDownEvent(engine)
  // registerMouseUpEvent(engine)
  // registerTouchEventsV2(engine)

  // const isStateDirty = createIsStateDirtyMemo(engine)
  // useConfirmLeaveWithUnsavedChanges(isStateDirty)

  useCornerWidgetEngine(engine)

  createRedirectIfWrongStateTypeEffect(componentStateType, () => engine.state.type, () => engine.state.online?.id)

  setEngineToast(engine, toast)

  const canvasElem = createMountedMainCanvasElement()
  const renderer = createMountedMainRenderer(canvasElem)
  setEngineThreeMainRenderer(engine, renderer)
  const camera = createMainCamera(canvasElem)
  setEngineThreeMainCamera(engine, camera)

  bindViewCtxToWindowV2(engine, canvasElem)
  bindEngineToComponentV2(engine, canvasElem)
  bindCameraToUserPreferences(engine)

  onMountStartCameraNearLastAction(engine)

  return <EngineContext.Provider value={engine}>
    <div id="gpc">
      <div id="gvp" ref={(ref: HTMLElement) => {
        // console.log('ref', ref)
        if (ref) {
          registerTouchEventsV2(engine, ref)
          // registerMouseMoveEvent(engine, ref)
        }
      }}>
        {canvasElem}
      </div>
    </div>
    <GameEndedModal />
  </EngineContext.Provider>
}

export default GamePlayThreeJsCanvas
